import React, {useMemo, useRef} from 'react';
import {useSelector} from 'react-redux';
import {useReactToPrint} from 'react-to-print';
import {useTranslation} from 'react-i18next';
import {selectedPresentationSelector, selectedPresentationSlidePreviewsSelector} from '../../../../store/selectors/presentation.selectors';
import Modal from '../../../../incase-domain/components/modals/directory/Modal';
import {WORKSPACE_SIZE_DEFAULT} from '../../../../constants';

interface ExportToPdfModalProps {
    isOpen: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

const PDF_PAGE_LONG_EDGE_MM = 297;

function ExportToPdfModal(props: ExportToPdfModalProps): JSX.Element {
    const {isOpen, onCancel, onConfirm} = props;

    const {t} = useTranslation();

    const selectedPresentation = useSelector(selectedPresentationSelector);
    const selectedPresentationPreviews = useSelector(selectedPresentationSlidePreviewsSelector);

    const slidesContainer = useRef<HTMLDivElement>(null);

    const pageStyle = useMemo(() => {
        const {width, height} = selectedPresentation.workspaceSize || WORKSPACE_SIZE_DEFAULT;
        const ratio = width / height;
        const pageWidthMm = ratio >= 1 ? PDF_PAGE_LONG_EDGE_MM : PDF_PAGE_LONG_EDGE_MM * ratio;
        const pageHeightMm = ratio >= 1 ? PDF_PAGE_LONG_EDGE_MM / ratio : PDF_PAGE_LONG_EDGE_MM;

        return `
            @page {
                size: ${pageWidthMm}mm ${pageHeightMm}mm;
                margin: 0;
            }
            img {
                display: block;
                width: 100%;
                height: auto;
                page-break-after: always;
                page-break-inside: avoid;
            }
            img:last-child {
                page-break-after: auto;
            }
        `;
    }, [selectedPresentation.workspaceSize]);

    const handlePrint = useReactToPrint({
        content: () => slidesContainer.current,
        documentTitle: selectedPresentation.name?.trim() || 'NCC',
        // Avoid copying app print styles (@page margins) that leave room for browser
        // headers/footers (title, date, page number, about:srcdoc).
        copyStyles: false,
        pageStyle,
    });

    function handleCancel(): void {
        onCancel();
    }

    function handleConfirm(): void {
        handlePrint();
        onConfirm();
    }

    return (
        <Modal
            isOpen={isOpen}
            size="lg"
            onCancel={handleCancel}
            onConfirm={handleConfirm}
            header={selectedPresentation.name}
            description={t('common.exportToPdf')}
            icon="ph:file-pdf"
            showConfirm
            confirmText={t('common.confirm')}
        >
            <div style={{maxHeight: '65vh', overflowY: 'auto'}} className="u-p-2">
                <div ref={slidesContainer}>
                    {}

                    {selectedPresentation.slides.map((slide) => {
                        const preview = selectedPresentationPreviews.find((item) => item.slideId === slide._id);
                        if (!preview) return null;
                        return <img key={`export-print-preview-${preview.slideId}`} src={preview.imgSrc} alt="slide-preview" />;
                    })}
                </div>
            </div>
        </Modal>
    );
}

export default ExportToPdfModal;
